import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Sidebar from "./Sidebar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const API = "https://resume-maker-lc.onrender.com/api";

// ─── Helper: safely parse a JSON string, returning fallback on error ──────────
const safeParseJSON = (str, fallback) => {
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && parsed !== undefined ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export default function SummarySection() {
  const [search, setSearch] = useState("");
  const [content, setContent] = useState("");
  const [summaries, setSummaries] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const textInputRef = useRef(null);

  // ─── Initialize template & theme from location.state first,
  //     then fall back to localStorage so back-navigation never loses the choice ─
  const getInitialTemplate = () => {
    if (location.state?.selectedTemplate) return location.state.selectedTemplate;
    return safeParseJSON(localStorage.getItem("selectedTemplate"), 1);
  };

  const getInitialTheme = () => {
    if (location.state?.selectedTheme) return location.state.selectedTheme;
    return safeParseJSON(localStorage.getItem("selectedTheme"), "#1a2b47");
  };

  const [selectedTemplate, setSelectedTemplate] = useState(getInitialTemplate);
  const [selectedTheme, setSelectedTheme] = useState(getInitialTheme);

  // ─── Persist template & theme to localStorage whenever they change ────────
  useEffect(() => {
    localStorage.setItem("selectedTemplate", JSON.stringify(selectedTemplate));
  }, [selectedTemplate]);

  useEffect(() => {
    localStorage.setItem("selectedTheme", JSON.stringify(selectedTheme));
  }, [selectedTheme]);

  // ─── Sync from location.state when navigation supplies fresh values ────────
  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: tmpl, selectedTheme: thm } = location.state;
      if (tmpl) {
        setSelectedTemplate(tmpl);
        localStorage.setItem("selectedTemplate", JSON.stringify(tmpl));
      }
      if (thm) {
        setSelectedTheme(thm);
        localStorage.setItem("selectedTheme", JSON.stringify(thm));
      }
    }
  }, [location.state]);

  const popularJobs = [
    "Cashier", "Customer Service Representative", "Manager", "Server",
    "Retail", "Web Developer", "Software Engineer", "Project Manager",
    "Data Analyst", "Marketing Specialist",
  ];

  // Load saved summaries from backend
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/summary/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSummaries(data);
        }
      } catch (err) {
        console.error("Failed to load summaries:", err);
        const saved = JSON.parse(localStorage.getItem("summaries")) || [];
        setSummaries(saved.map((s) => ({ _id: null, description: s })));
      }
    };
    fetchSummaries();
  }, []);

  // Sync summaries to localStorage whenever summaries state changes
  useEffect(() => {
    if (summaries.length > 0) {
      const descriptions = summaries.map((s) => s.description);
      localStorage.setItem("summaries", JSON.stringify(descriptions));
    } else {
      localStorage.setItem("summaries", JSON.stringify([]));
    }
  }, [summaries]);

  const handleAddSuggestion = (text) => {
    setContent(text);
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
      }
    }, 100);
  };

  const handleContentChange = (event) => {
    const newValue = event.target.value;
    setContent(newValue);
  };

  // ── AI Search ─────────────────────────────────────────────────────────────
  const handleSearch = async (jobTitle) => {
    const title = (jobTitle || search).trim();
    if (!title) {
      setSnackbar({ open: true, message: "Please enter a job title to search.", severity: "warning" });
      return;
    }

    setLoading(true);
    setSuggestions([]);

    try {
      const res = await fetch(`${API}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobTitle: title,
          count: 5,
        }),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      console.log(`📊 Received ${data.summaries?.length || 0} summaries from backend`);

      const fullSuggestions = (data.summaries || []).map((s) =>
        typeof s === "string" ? s.replace(/\.{3,}$/, "").trim() : ""
      ).filter(Boolean);

      setSuggestions(fullSuggestions);
      console.log(`📝 Displaying ${fullSuggestions.length} suggestions`);
    } catch (err) {
      console.error("Error fetching AI suggestions:", err);
      setSnackbar({ open: true, message: "Failed to fetch AI suggestions. Please try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // ── Save summary ──────────────────────────────────────────────────────────
  const handleSaveSummary = async () => {
    if (!content.trim()) {
      setSnackbar({ open: true, message: "Please write something before saving.", severity: "warning" });
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/summary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ description: content }),
      });

      if (res.ok) {
        const saved = await res.json();
        setSummaries((prev) => [...prev, saved]);
        setContent("");
        setSnackbar({ open: true, message: "Summary saved successfully!", severity: "success" });
      } else {
        throw new Error("Save failed");
      }
    } catch (err) {
      console.error("Save error:", err);
      const newEntry = { _id: null, description: content };
      setSummaries((prev) => [...prev, newEntry]);
      setContent("");
      setSnackbar({ open: true, message: "Summary saved locally!", severity: "info" });
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (index) => {
    const item = summaries[index];
    try {
      if (item._id) {
        const token = localStorage.getItem("token");
        await fetch(`${API}/summary/${item._id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
    setSummaries((prev) => prev.filter((_, i) => i !== index));
    setSnackbar({ open: true, message: "Summary deleted successfully!", severity: "success" });
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (index) => {
    setContent(summaries[index].description);
    handleDelete(index);
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
      }
    }, 100);
  };

  // ── Save & Next ───────────────────────────────────────────────────────────
  const handleSaveAndNext = async () => {
    if (content.trim()) {
      await handleSaveSummary();
    }
    if (summaries.length === 0 && !content.trim()) {
      setSnackbar({ open: true, message: "Please create and save at least one summary before proceeding.", severity: "warning" });
      return;
    }
    navigate("/ExtraSection", { state: { selectedTemplate, selectedTheme } });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ─── Go Back — always pass the persisted template & theme ─────────────────
  const handleGoBack = () => {
    navigate("/ResumeSummary", {
      state: { selectedTemplate, selectedTheme },
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      <Box
        sx={{
          ml: { xs: "70px", sm: "240px" },
          width: { xs: "calc(100% - 60px)", sm: "calc(100% - 240px)" },
          minHeight: "100vh",
          p: { xs: 1.5, sm: 3 },
          boxSizing: "border-box",
          backgroundColor: "#EFF7FF",
        }}
      >
        {/* Go Back Button */}
        <Typography
          variant="body2"
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mb: 1,
            cursor: "pointer",
            "&:hover": {
              color: "#0288D1",
              textDecoration: "underline",
            },
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
          }}
          onClick={handleGoBack}
        >
          ← Go Back
        </Typography>

        {/* Title */}
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: isSmallMobile ? "1.25rem" : "inherit",
            color: "#002B5B",
          }}
        >
          Briefly tell us about your
        </Typography>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: isSmallMobile ? "1.5rem" : "inherit",
            color: "#002B5B",
            borderBottom: "3px solid #0288D1",
            pb: 1,
          }}
        >
          Background
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          color="text.secondary"
          gutterBottom
          sx={{
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
            lineHeight: 1.6,
            color: "#0D4C8B",
          }}
        >
          Choose from our AI-powered examples below or write your own.
        </Typography>

        <Typography
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mt: 2,
            fontSize: isSmallMobile ? "0.875rem" : "inherit",
          }}
        >
          ✦ Our AI now makes writing easier!
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{
            fontSize: isSmallMobile ? "0.8rem" : "inherit",
            lineHeight: 1.6,
            color: "#0D4C8B",
          }}
        >
          We'll help you fix mistakes or rephrase sentences to suit your needs.
        </Typography>

        {/* Search */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, mt: 3, flexWrap: "wrap", gap: 1 }}>
          <TextField
            fullWidth
            placeholder="e.g. Software Engineer, Data Analyst..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            size={isSmallMobile ? "small" : "medium"}
            sx={{
              width: isSmallMobile ? "100%" : "400px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#B3D9F7" },
                "&:hover fieldset": { borderColor: "#B3D9F7" },
                "&.Mui-focused fieldset": { borderColor: "#002B5B", borderWidth: 2 },
              },
            }}
          />
          <IconButton
            onClick={() => handleSearch()}
            disabled={loading}
            sx={{
              ml: isSmallMobile ? 0 : 1,
              color: "#002B5B",
              "&:hover": { color: "#0288D1" },
            }}
          >
            {loading ? <CircularProgress size={20} /> : <SearchIcon />}
          </IconButton>
        </Box>

        {/* Popular Job Chips */}
        <Box sx={{ mb: 3, width: "100%", maxWidth: "800px" }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold", color: "#002B5B" }}>
            Popular Job Titles
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {popularJobs.map((job) => (
              <Chip
                key={job}
                label={job}
                size={isSmallMobile ? "small" : "medium"}
                sx={{
                  cursor: "pointer",
                  borderColor: "#B3D9F7",
                  "&:hover": {
                    backgroundColor: "#002B5B",
                    color: "white",
                  },
                  transition: "all 0.2s ease",
                }}
                onClick={() => {
                  setSearch(job);
                  setTimeout(() => handleSearch(job), 0);
                }}
              />
            ))}
          </Box>
        </Box>

        {/* AI Suggestions */}
        <Box sx={{ mb: 4, width: "100%" }}>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold", color: "#002B5B" }}>
            {suggestions.length > 0 ? `Showing ${suggestions.length} AI suggestions for` : "Search for"}{" "}
            <b style={{ color: "#0288D1" }}>{search || "___"}</b>
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#002B5B" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#0D4C8B" }}>
                Generating full paragraphs for <b>{search}</b>...
              </Typography>
            </Box>
          ) : suggestions.length > 0 ? (
            suggestions.map((s, i) => (
              <Card
                key={`suggestion-${i}`}
                sx={{
                  mb: 2,
                  borderLeft: "5px solid #002B5B",
                  border: "1px solid #B3D9F7",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,43,91,0.12)",
                    transform: isMobile ? "none" : "translateY(-2px)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: isSmallMobile ? 2 : 3,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{
                      minWidth: "80px",
                      flexShrink: 0,
                      backgroundColor: "#002B5B",
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": { backgroundColor: "#0288D1" },
                    }}
                    onClick={() => handleAddSuggestion(s)}
                  >
                    Add
                  </Button>

                  <Box sx={{ flex: 1, minWidth: "200px" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#0288D1" }}>
                      ⭐ AI Suggested #{i + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.9,
                        whiteSpace: "pre-wrap",
                        color: "#0D4C8B",
                        fontSize: "0.875rem",
                      }}
                    >
                      {s}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                border: "2px dashed #B3D9F7",
                width: "100%",
                maxWidth: "900px",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="#0D4C8B">
                {search
                  ? "No suggestions found. Try a different job title."
                  : "Enter a job title above to get AI-powered summary suggestions."}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Editor Panel */}
        <Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold", color: "#002B5B" }}>
            Your Summary
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#0D4C8B" }}>
            Add a suggestion or write your own. Aim for 4–5 sentences.
          </Typography>

          <textarea
            ref={textInputRef}
            id="desc-box"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="e.g. Experienced Software Engineer with 5+ years of expertise in building scalable web applications..."
            style={{
              width: "100%",
              minHeight: isMobile ? "150px" : "200px",
              padding: "12px",
              fontSize: isSmallMobile ? "14px" : "16px",
              lineHeight: 1.8,
              fontFamily: "inherit",
              border: "2px solid #B3D9F7",
              borderRadius: "4px",
              backgroundColor: "white",
              marginBottom: "16px",
              resize: "vertical",
              outline: "none",
              transition: "border-color 0.3s ease",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#002B5B"; }}
            onBlur={(e) => { e.target.style.borderColor = "#B3D9F7"; }}
          />

          <Typography variant="caption" sx={{ mb: 2, display: "block", color: "#0D4C8B" }}>
            {content.length} characters · {content.trim().split(/[.!?]+/).filter(Boolean).length} sentences
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 20,
                px: 3,
                borderColor: "#002B5B",
                color: "#002B5B",
                "&:hover": {
                  borderColor: "#0288D1",
                  color: "#0288D1",
                  backgroundColor: "rgba(2,136,209,0.04)",
                },
              }}
              onClick={handleSaveSummary}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#002B5B",
                borderRadius: 20,
                px: 4,
                "&:hover": { bgcolor: "#0288D1" },
              }}
              onClick={handleSaveAndNext}
            >
              Save & Next
            </Button>
          </Box>

          {/* Saved Summaries */}
          <Box mt={4}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#002B5B" }}>
              Saved Summaries ({summaries.length})
            </Typography>

            {summaries.length > 0 ? (
              summaries.map((s, i) => (
                <Card
                  key={`saved-${i}-${s._id || i}`}
                  sx={{
                    mb: 2,
                    border: "1px solid #B3D9F7",
                    borderLeft: "5px solid #002B5B",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,43,91,0.12)" },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.8, whiteSpace: "pre-wrap", color: "#0D4C8B" }}
                    >
                      {s.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "flex-end", gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(i)} size="small">
                        <EditIcon sx={{ color: "#0288D1" }} fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => {
                          if (window.confirm("Delete this summary?")) handleDelete(i);
                        }}
                        size="small"
                      >
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  border: "1px dashed #B3D9F7",
                  borderRadius: 1,
                  backgroundColor: "white",
                }}
              >
                <Typography variant="body2" color="#0D4C8B">
                  No saved summaries yet.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}